/**
 * PR-1033: evidence receipt types and the plain-text form used for copy and print.
 * Wording follows decision 73: "Required", never framework names.
 */

export interface ReceiptFact {
  key: string;
  value: unknown;
  status: string;
  method: string | null;
  target: string | null;
  kind: string | null;
  valueHash: string | null;
  collectedUtc: string | null;
  failureReason: string | null;
}

export interface Receipt {
  receiptVersion: number;
  executionId: string;
  audit: { completedAt: string | null; agentVersion: string | null; host: string | null };
  module: { nodeId: string; name: string; status: string | null };
  check: {
    id: string;
    name: string;
    status: 'pass' | 'warn' | 'fail' | 'info' | 'not_assessed';
    reportedStatus: string;
    required: unknown;
    current: unknown;
    severity: string | null;
    observed: unknown;
    observedCount: number | null;
    observedTruncated: boolean;
    provenance: 'observed' | 'inferred' | 'missing' | null;
    basis: string | null;
    source: { kind: string; target: string | null } | null;
  };
  evidence: 'recorded' | 'legacy';
  crossCheck: { outcome: string; note: string };
  facts: ReceiptFact[];
  redaction: { redacted: boolean; replaced: Record<string, number> };
  hashAlgorithm: string;
  receiptHash: string;
}

export const RESULT_LABEL: Record<Receipt['check']['status'], { label: string; text: string }> = {
  pass: { label: 'Compliant', text: 'text-green-400' },
  warn: { label: 'Needs Attention', text: 'text-yellow-400' },
  fail: { label: 'Non-Compliant', text: 'text-red-400' },
  info: { label: 'Informational', text: 'text-white/50' },
  not_assessed: { label: 'Not assessed', text: 'text-slate-300' },
};

export const PROVENANCE: Record<'observed' | 'inferred' | 'missing', { label: string; cls: string; meaning: string }> = {
  observed: { label: 'OBSERVED', cls: 'bg-sky-400/10 text-sky-300 border-sky-400/20', meaning: 'Read from the machine' },
  inferred: { label: 'INFERRED', cls: 'bg-amber-400/10 text-amber-300 border-amber-400/20', meaning: 'Not read: a documented default was applied' },
  missing: { label: 'NOT COLLECTED', cls: 'bg-slate-400/10 text-slate-300 border-slate-400/20', meaning: 'The value could not be read, so the check is not assessed' },
};

export function formatValue(value: unknown, provenance: Receipt['check']['provenance']): string {
  if (value === null || value === undefined) {
    return provenance === 'inferred' || provenance === 'missing' ? '(value not present)' : '(none)';
  }
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value, null, 2);
}

export function shortHash(hash: string | null): string {
  return hash ? `${hash.slice(0, 8)}...${hash.slice(-8)}` : '(none)';
}

export function when(iso: string | null): string {
  return iso ? `${iso.replace('T', ' ').slice(0, 16)} UTC` : '(not recorded)';
}

/** Plain-text receipt for copy and print. */
export function receiptText(r: Receipt): string {
  const c = r.check;
  const lines = [
    `EVIDENCE RECEIPT  ${c.id}`,
    `Check       ${c.name}  (${r.module.name})`,
    `Result      ${RESULT_LABEL[c.status].label}`,
    `Required    ${formatValue(c.required, null)}`,
    `Observed    ${formatValue(c.observed, c.provenance).replace(/\n/g, '\n            ')}`,
    `Provenance  ${c.provenance ? PROVENANCE[c.provenance].label : 'not recorded'}${c.basis ? ` - ${c.basis}` : ''}`,
    `Collected   ${c.source ? `${c.source.kind}  ${c.source.target ?? ''}`.trim() : 'not recorded'}`,
    `Recorded    ${when(r.audit.completedAt)}`,
    `Host        ${r.audit.host ?? '(not recorded)'}`,
    `Agent       ${r.audit.agentVersion ?? '(version not recorded)'}`,
    `Server      ${r.crossCheck.note}`,
  ];
  if (r.facts.length > 0) {
    lines.push('', 'Audit context (pre-flight facts):');
    for (const f of r.facts) {
      const value = f.status === 'collected' ? formatValue(f.value, 'observed').replace(/\s+/g, ' ') : `(${f.status}${f.failureReason ? `: ${f.failureReason}` : ''})`;
      lines.push(`  ${f.key} = ${value}  [${[f.method, f.target].filter(Boolean).join(' ')}]${f.valueHash ? `  sha256 ${shortHash(f.valueHash)}` : ''}`);
    }
  }
  lines.push('', `Redacted    ${r.redaction.redacted ? 'yes' : 'NO - contains identifiers'}`);
  lines.push(`Receipt     sha256 ${r.receiptHash}`);
  lines.push(`            ${r.hashAlgorithm}`);
  return lines.join('\n');
}

